import { Link } from "@tanstack/react-router";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import type { BlogArticle } from "@/data/blog";
import { BOOKING_URL_ALL } from "@/data/blog";
import { blocksBySlug } from "@/data/blog.body";
import { track } from "@/lib/analytics";
import { RelatedGuidesBlock } from "@/components/RelatedGuidesBlock";
import { autoLink, createAutoLinkCtx } from "@/lib/autoLink";

interface Props {
  article: BlogArticle;
}

export function BlogArticleTemplate({ article }: Props) {
  const linkCtx = createAutoLinkCtx(`/${article.slug}/`);
  return (
    <article className="min-h-screen bg-background">
      <div className="container-page py-4">
        <nav aria-label="Brödsmulor" className="flex items-center gap-2 text-sm text-foreground/50">
          <Link to="/" className="hover:text-foreground transition-colors">Hem</Link>
          <span>/</span>
          <Link to="/blogg/" className="hover:text-foreground transition-colors">Inspiration</Link>
          <span>/</span>
          <span className="text-foreground/70 truncate">{article.title}</span>
        </nav>
      </div>

      <header className="container-page pb-8">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-medium uppercase tracking-widest text-brass/80 border border-brass/30 rounded px-2 py-0.5">
              {article.category}
            </span>
            <span className="text-sm text-foreground/50">
              {new Date(article.publishedAt).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" })}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-lg md:text-xl text-foreground/70 leading-relaxed">
            {article.metaDescription}
          </p>
        </div>
      </header>

      <section className="container-page pb-8">
        <div className="max-w-2xl">
          <p className="text-base md:text-lg leading-relaxed text-foreground/85">{autoLink(article.intro, linkCtx)}</p>
        </div>
      </section>

      <section className="container-page pb-12">
        <div className="max-w-2xl space-y-5">
          {(blocksBySlug[article.slug] ?? []).map((block, i) => {
            switch (block.type) {
              case "h2":
                return (
                  <h2 key={i} className="text-2xl md:text-3xl font-semibold mt-6">
                    {block.text}
                  </h2>
                );
              case "h3":
                return (
                  <h3 key={i} className="text-xl md:text-2xl font-semibold mt-4">
                    {block.text}
                  </h3>
                );
              case "p":
                return (
                  <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/80">
                    {autoLink(block.text, linkCtx)}
                  </p>
                );
              case "ul":
                return (
                  <ul key={i} className="list-disc pl-6 space-y-2 text-base md:text-lg text-foreground/80">
                    {block.items.map((it, j) => (
                      <li key={j} className="leading-relaxed">{autoLink(it, linkCtx)}</li>
                    ))}
                  </ul>
                );
              case "img":
                return (
                  <img
                    key={i}
                    src={block.src}
                    alt={block.alt}
                    loading="lazy"
                    className="w-full aspect-[16/10] object-cover rounded-lg my-4"
                  />
                );
              case "figure":
                return (
                  <figure key={i} className="my-6 max-w-md">
                    <img
                      src={block.src}
                      alt={block.alt}
                      loading="lazy"
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    <figcaption className="mt-2 text-sm text-foreground/60">{block.caption}</figcaption>
                  </figure>
                );
              case "figures":
                return (
                  <div key={i} className={`my-6 grid gap-4 ${block.items.length > 1 ? "grid-cols-2 sm:grid-cols-3" : ""}`}>
                    {block.items.map((f, j) => (
                      <figure key={j}>
                        <img
                          src={f.src}
                          alt={f.alt}
                          loading="lazy"
                          className="w-full aspect-square object-cover rounded-lg"
                        />
                        <figcaption className="mt-2 text-sm text-foreground/60">{f.caption}</figcaption>
                      </figure>
                    ))}
                  </div>
                );
            }
          })}
        </div>
      </section>

      {article.faq.length > 0 && (
        <section className="container-page py-12 border-t border-border">
          <div className="max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-semibold mb-6">Vanliga frågor</h2>
            <Accordion type="single" collapsible className="w-full">
              {article.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base md:text-lg">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-foreground/80 leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      <section className="container-page py-14 border-t border-border">
        <div className="max-w-2xl mx-auto text-center rounded-2xl border border-brass/30 bg-brass/5 p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-semibold mb-3">Redo att boka din klippning?</h2>
          <p className="text-foreground/70 mb-6">Välj din närmaste salong och boka direkt online.</p>
          <a
            href={BOOKING_URL_ALL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("book_now_click", { cta_location: `article_footer:${article.slug}` })}
            className="btn-brass inline-flex items-center gap-2 px-6 py-3 text-base font-semibold"
          >
            Boka tid →
          </a>
        </div>
      </section>

      <RelatedGuidesBlock slug={article.slug} />
    </article>
  );
}
